import { parseCardUpgrades, getEffectiveCardStatsFromUpgrades } from '../utils/cardUpgrades';
import GLOBALS from '../config/globals';

/**
 * Returns effective pitcher stamina for a card, accounting for upgrades.
 * Accepts upgrades as raw (from CardInventory.upgrades) — parses internally.
 */
export const getEffectivePitcherStamina = (card, upgradesRaw) => {
    const upgrades = parseCardUpgrades(upgradesRaw);
    const { effectivePitcherStamina } =
        getEffectiveCardStatsFromUpgrades({ card, upgrades, globals: GLOBALS });
    return effectivePitcherStamina ?? card?.stamina ?? 27;
};

/**
 * Returns effective batter speed for a card, accounting for upgrades.
 */
export const getEffectiveBatterSpeed = (card, upgradesRaw) => {
    const upgrades = parseCardUpgrades(upgradesRaw);
    const { effectiveBatterSpeed } =
        getEffectiveCardStatsFromUpgrades({ card, upgrades, globals: GLOBALS });
    return effectiveBatterSpeed ?? card?.speed ?? null;
};

/**
 * Formats a runner's display string with effective speed: "Year Name - Speed X (+Y)"
 * `participantLineups` is the GameLineups array from a GameParticipant (has CardInventory).
 */
export const formatRunnerWithSpeed = (lineup, participantLineups) => {
    const card = lineup?.Card;
    if (!card) return null;
    const gl = participantLineups?.find(g => String(g.gameLineupID) === String(lineup.gameLineupID));
    const baseSpeed = card.speed;
    const effSpeed = getEffectiveBatterSpeed(card, gl?.CardInventory?.upgrades);
    const delta = effSpeed != null && baseSpeed != null ? effSpeed - baseSpeed : 0;
    const speedText = delta > 0 ? `${baseSpeed} (+${delta})` : `${baseSpeed}`;
    return `${card.year} ${card.name} - Speed ${speedText}`;
};

export const sortCardsByRating = cards => {
    return cards.sort((a, b) => b.Card.rating - a.Card.rating);
};

export const getCardOptions = (position, index = null, userCards, lineup, selectedCards, cardsByInventoryID) => {
    let filteredCards;

    if (['DH', 'Bench'].includes(position)) {
        filteredCards = userCards.filter(
            ({ Card }) => Card && !Card.positions.includes('SP') && !Card.positions.includes('RP')
        );
    } else if (['LF', 'CF', 'RF'].includes(position)) {
        filteredCards = userCards.filter(({ Card }) => Card && Card.positions.includes('OF'));
    } else {
        filteredCards = userCards.filter(({ Card }) => Card && Card.positions.includes(position));
    }

    // Build set of playerIDs already in lineup (from selected inventoryIDs)
    const selectedPlayerIDs = new Set();
    for (const invID of selectedCards) {
        const ci = cardsByInventoryID?.get(invID);
        if (ci?.Card?.playerID) selectedPlayerIDs.add(ci.Card.playerID);
    }

    // The current slot's playerID should not be excluded
    const currentInventoryID = Array.isArray(lineup[position])
        ? lineup[position][index]?.inventoryID
        : lineup[position]?.inventoryID;
    const currentPlayerID = currentInventoryID
        ? cardsByInventoryID?.get(currentInventoryID)?.Card?.playerID
        : null;

    const availableCards = filteredCards.filter(({ Card }) => {
        if (!Card) return false;
        const isCurrentPlayer = currentPlayerID && Card.playerID === currentPlayerID;
        return isCurrentPlayer || !selectedPlayerIDs.has(Card.playerID);
    });

    return sortCardsByRating(availableCards).map(({ inventoryID, Card, CardVariation }) => ({
        key: inventoryID,
        value: inventoryID,
        label: CardVariation
            ? `${Card.year} ${Card.name} (${Card.rating}) (${Card.positions}) - ${CardVariation.name}`
            : `${Card.year} ${Card.name} (${Card.rating}) (${Card.positions})`,
    }));
};

export const calculateScore = (lineup, userCards, cardsByInventoryID) => {
    let totalRating = 0;
    let count = 0;

    const addRating = inventoryID => {
        if (inventoryID) {
            const card = cardsByInventoryID?.get(inventoryID);
            if (card) {
                totalRating += card.Card.rating;
                count += 1;
            }
        }
    };

    Object.values(lineup).forEach(position => {
        if (Array.isArray(position)) {
            position.forEach(({ inventoryID }) => addRating(inventoryID));
        } else {
            addRating(position.inventoryID);
        }
    });

    return count > 0 ? Math.round(totalRating / count) : 0;
};

/**
 * Auto-picks the best available substitute for each fatigued player.
 * Greedy by rating — highest rated sub is picked first, then excluded from
 * remaining slots so the same card can't fill two positions.
 *
 * @param {Array} availableSubstitutes - from PLAYERS_FATIGUED response
 * @returns {Object} picks - { forInventoryID: toInventoryID }
 */
export const autoPickSubstitutions = availableSubstitutes => {
    const usedInventoryIDs = new Set();
    const picks = {};

    for (const group of availableSubstitutes) {
        const sorted = [...group.substitutes].sort((a, b) => b.rating - a.rating);
        const best = sorted.find(sub => !usedInventoryIDs.has(sub.inventoryID));
        if (best) {
            usedInventoryIDs.add(best.inventoryID);
            picks[group.forInventoryID] = best.inventoryID;
        }
    }

    return picks;
};

export const lineupOrderOptions = [1, 2, 3, 4, 5, 6, 7, 8, 9];

export const positions = [
    { label: 'C', type: 'single' },
    { label: '1B', type: 'single' },
    { label: '2B', type: 'single' },
    { label: '3B', type: 'single' },
    { label: 'SS', type: 'single' },
    { label: 'LF', type: 'single' },
    { label: 'CF', type: 'single' },
    { label: 'RF', type: 'single' },
    { label: 'DH', type: 'single' },
    { label: 'SP', type: 'array', count: 5 },
    { label: 'RP', type: 'array', count: 5 },
    { label: 'Bench', type: 'array', count: 5 },
];

/**
 * Creates an empty lineup structure with proper position slots
 * @returns {Object} Initial lineup with all positions set to empty values
 */
const VARIATION_ABBREV = {
    'Black & White': 'BW',
    'Negative': 'N',
    'Chrome': 'C',
    'Refractor': 'R',
    'Fire': 'F',
};

export const getVariationAbbrev = (card) => {
    const name = card?.CardInventories?.[0]?.CardVariation?.name;
    if (!name) return null;
    return VARIATION_ABBREV[name] ?? name.charAt(0);
};

export const createInitialLineup = () => {
    const initialLineup = {};

    positions.forEach(({ label, type, count }) => {
        if (type === 'single') {
            initialLineup[label] = { inventoryID: '', cardID: '', lineupOrder: '' };
        } else {
            initialLineup[label] = Array.from({ length: count }, () => ({
                inventoryID: '',
                cardID: '',
                lineupOrder: ''
            }));
        }
    });

    return initialLineup;
};
